import type { IEvent } from "../../interfaces/event";

const toIsoString = (value: Date | string | null | undefined): string | null => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  return String(value);
};

function minutesBetween(start?: string | Date | null, end?: string | Date | null): number {
  try {
    if (!start || !end) return 0;
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return 0;
    return Math.round((e - s) / 60000);
  } catch {
    return 0;
  }
}

function getRequiredSkillNames(raw: IEvent): string[] {
  const characteristics = raw?.incidentType?.characteristic ?? [];
  return characteristics
    .map((c) => c?.name)
    .filter((v): v is string => typeof v === "string" && v.length > 0);
}

function getSiteLocation(raw: IEvent): string | null {
  const fromSite = raw?.siteLocationDetails?.name;
  if (typeof fromSite === "string" && fromSite.length > 0) return fromSite;
  const fromAccount = raw?.serviceAccount?.name;
  if (typeof fromAccount === "string" && fromAccount.length > 0) return fromAccount;
  return null;
}

// Normalizes an unassigned job to AIEmpower Unassigned Job shape
export function normalizeUnassignedJob(raw: IEvent): any {
  const start = toIsoString(raw?.start);
  const end = toIsoString(raw?.end) ?? start;

  return {
    jobId: raw?.id ?? "",
    title: raw?.title ?? "",
    category: raw?.incidentType?.name ?? raw?.jobType ?? null,
    requiredSkills: getRequiredSkillNames(raw),
    preferredResourceId: raw?.preferredResourceId ?? null,
    preferredResourceName: raw?.preferredResourceName ?? null,
    isJobBidding: !!raw?.isJobBidding,
    jobType: raw?.jobType ?? null,
    workOrderType: raw?.workOrderType?.msdyn_name ?? null,
    workOrderTypeId: raw?.workOrderType?.msdyn_workordertypeid ?? null,
    hourlyRate: raw?.hourlyRate ?? null,
    estimatedTravelDuration: raw?.estimatedTravelDuration ?? null,
    products: Array.isArray(raw?.products) ? raw.products : [],
    services: Array.isArray(raw?.services) ? raw.services : [],
    displayCost: raw?.displayCost ?? null,
    actualCost: raw?.actualCost ?? null,
    siteLocation: getSiteLocation(raw),
    siteCoordinates: {
      latitude: raw?.siteLocationDetails?.latitude ?? null,
      longitude: raw?.siteLocationDetails?.longitude ?? null
    },
    siteLocationDetails: raw?.siteLocationDetails ?? null,
    serviceAccount: raw?.serviceAccount ?? null,
    start,
    end,
    durationMinutes: minutesBetween(start, end),
    // Optional fields that may be populated in future mappings
    priority: null,
    notes: raw?.description ?? null
  };
}
